import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { PopoverMenuComponent } from './popover-menu.component';

/**
 * A single item inside `<app-popover-menu>`. Renders a native `<button role="menuitem">`
 * with optional icon, disabled state, and destructive variant. Activating the item fires
 * `(select)` and automatically closes the parent menu.
 *
 * @feature shared
 */
@Component({
  selector: 'app-popover-menu-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'block' },
  template: `
    <button
      type="button"
      role="menuitem"
      [disabled]="disabled()"
      (click)="activate()"
      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
      [class.text-body]="!destructive() && !disabled()"
      [class.text-danger]="destructive() && !disabled()"
      [class.text-disabled]="disabled()"
      [class.hover:bg-surface-hover]="!disabled()"
      [class.cursor-not-allowed]="disabled()"
    >
      @if (icon()) {
        <app-icon [name]="icon()!" size="xs" />
      }
      <span class="flex-1"><ng-content /></span>
    </button>
  `,
})
export class PopoverMenuItemComponent {
  private readonly parent = inject(PopoverMenuComponent, { optional: true });

  /** Optional leading icon name. */
  readonly icon = input<string | undefined>(undefined);

  /** Disables the menu item. */
  readonly disabled = input<boolean>(false);

  /** Renders the item in the danger color for destructive actions. */
  readonly destructive = input<boolean>(false);

  /** Fires when the item is activated. */
  readonly select = output<void>();

  activate(): void {
    if (this.disabled()) return;
    this.select.emit();
    this.parent?.onItemSelected();
  }
}
